import type { DashboardChartSourceKey, DashboardCustomMetricDefinition, LinkedWidgetEntry } from '../types';

import { DASHBOARD_REVIEW_SOURCE_CATALOG } from './dashboard-catalog.data';

export interface DropConfigTextPreset {
  readonly key: string;
  readonly label: string;
  readonly icon: string;
  readonly description: string;
  readonly heading: string;
  readonly body: string;
}

export const DROP_CONFIG_TEXT_PRESETS: ReadonlyArray<DropConfigTextPreset> = [
  {
    key: 'ai-summary',
    label: 'AI Summary',
    icon: 'auto_awesome',
    description: 'Auto-generated analysis from your latest reviews',
    heading: 'AI Guest Insights',
    body: "Based on 847 reviews this quarter, guests consistently highlight warm, attentive service as this property's standout quality. Cleanliness scores average 4.7/5 across all room categories. The most frequently cited concern is check-in wait times during peak arrival windows. Breakfast receives strong praise, particularly the made-to-order options. Room comfort ranks highly, though corridor noise is a recurring mention in lower-floor rooms. Overall NPS is up +8 points versus last quarter, driven largely by repeat visitors in the leisure segment.",
  },
];

export interface CustomMetricGroupItem {
  readonly label: string;
  readonly value: string;
}

export interface CustomMetricGroup {
  readonly label: string;
  readonly items: ReadonlyArray<CustomMetricGroupItem>;
}

export const CUSTOM_METRIC_GROUPS: ReadonlyArray<CustomMetricGroup> = [
  {
    label: 'Primary Service Rating',
    items: [
      { label: 'Overall Satisfaction',              value: 'primary_service_rating: overall_satisfaction' },
      { label: 'Value For Money',                   value: 'primary_service_rating: value_for_money' },
      { label: 'Would Recommend',                   value: 'primary_service_rating: would_recommend' },
      { label: 'Service Received',                  value: 'primary_service_rating: service_received' },
      { label: 'Cleanliness',                       value: 'primary_service_rating: cleanlines' },
      { label: 'Location',                          value: 'primary_service_rating: location' },
      { label: 'Sleep Quality',                     value: 'primary_service_rating: sleep_quality' },
      { label: 'Room Quality',                      value: 'primary_service_rating: room_quality' },
      { label: 'Food Quality',                      value: 'primary_service_rating: food_quality' },
      { label: 'Housekeeping',                      value: 'primary_service_rating: housekeeping' },
      { label: 'Atmosphere',                        value: 'primary_service_rating: atmosphere' },
      { label: 'Breakfast Service',                 value: 'primary_service_rating: breakfast_service' },
    ],
  },
  {
    label: 'Room Quality',
    items: [
      { label: 'Room Comfort and Decor',            value: 'room_quality: room_comfort_and_decor' },
      { label: 'Cleanliness',                       value: 'room_quality: cleanliness' },
      { label: 'Housekeeping',                      value: 'room_quality: housekeeping' },
      { label: 'Maintenance of Room',               value: 'room_quality: maintenance' },
      { label: 'Facilities and Amenities',          value: 'room_quality: facilities_and_amenities' },
    ],
  },
  {
    label: 'Restaurant and Bar',
    items: [
      { label: 'Restaurant Comfort and Decor',         value: 'restuarant_and_bar: restaurant_comfort_and_decor' },
      { label: 'Cleanliness',                          value: 'restuarant_and_bar: cleanliness' },
      { label: 'Menu Choice',                          value: 'restuarant_and_bar: menu_choice' },
      { label: 'Wine and Beverage Choice',             value: 'restuarant_and_bar: wine_and_beverage_choice' },
      { label: 'Waiter Knowledge and Professionalism', value: 'restuarant_and_bar: waiter_knowledge_and_professionalism' },
      { label: 'Waiting Period',                       value: 'restuarant_and_bar: waiting_period' },
      { label: 'Food Quality',                         value: 'restuarant_and_bar: food_quality_and_presentation' },
    ],
  },
  {
    label: 'Spa and Gym',
    items: [
      { label: 'Cleanliness',                          value: 'spa_and_gym: cleanliness' },
      { label: 'Treatments and Therapy Choice',        value: 'spa_and_gym: treatments_and_therapy_choice' },
      { label: 'Quality and Variety of Gym Equipment', value: 'spa_and_gym: quality_and_variety_of_gym_equipment' },
      { label: 'Sauna',                                value: 'spa_and_gym: sauna' },
      { label: 'Quality of Treatment',                 value: 'spa_and_gym: quality_of_treatment' },
      { label: 'Therapist Knowledge and Capability',   value: 'spa_and_gym: therapist_knowledge_and_capability' },
      { label: 'Spa Value for Money',                  value: 'spa_and_gym: spa_value_for_money' },
      { label: 'Therapist Friendly and Courteous',     value: 'spa_and_gym: therapist_friendly_and_courteous' },
    ],
  },
];

const ALL_SOURCES = DASHBOARD_REVIEW_SOURCE_CATALOG.map((s) => s.key);

export const OTA_PRIMARY_SOURCES: Readonly<Record<string, ReadonlyArray<string>>> = {
  'primary_service_rating: overall_satisfaction': ALL_SOURCES,
  'primary_service_rating: cleanlines':           ALL_SOURCES,
  'primary_service_rating: location':             ALL_SOURCES,
  'primary_service_rating: value_for_money':      ['guestrevu', 'tripadvisor', 'expedia', 'booking_com'],
  'primary_service_rating: service_received':     ['guestrevu', 'tripadvisor', 'expedia', 'booking_com'],
  'primary_service_rating: sleep_quality':        ['guestrevu', 'tripadvisor', 'expedia', 'booking_com'],
  'primary_service_rating: room_quality':         ['guestrevu', 'tripadvisor', 'expedia', 'booking_com'],
  'primary_service_rating: food_quality':         ['guestrevu', 'tripadvisor', 'booking_com'],
  'primary_service_rating: would_recommend':      ['guestrevu'],
  'primary_service_rating: housekeeping':         ['guestrevu'],
  'primary_service_rating: atmosphere':           ['guestrevu'],
  'primary_service_rating: breakfast_service':    ['guestrevu'],
};

export const GUESTREVU_ONLY_PREFIXES: ReadonlyArray<string> = [
  'room_quality:', 'restuarant_and_bar:', 'spa_and_gym:',
];

export function sourcesForQuestion(questionValue: string): ReadonlyArray<string> {
  if (OTA_PRIMARY_SOURCES[questionValue]) return OTA_PRIMARY_SOURCES[questionValue];
  if (GUESTREVU_ONLY_PREFIXES.some((p) => questionValue.startsWith(p))) return ['guestrevu'];
  return ALL_SOURCES;
}

function hashCode(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (Math.imul(31, h) + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}

export function buildCustomMetricDef(key: string, label: string): DashboardCustomMetricDefinition {
  const score = 70 + (hashCode(key) % 26);
  const delta = (hashCode(key + 'delta') % 11) - 5;
  const reviews = 20 + (hashCode(key + 'reviews') % 481);
  return {
    key,
    label,
    group: 'Custom Metrics',
    icon: 'quiz',
    value: `${score}%`,
    tone: 'success',
    description: `${label} survey rating`,
    badge: {
      value: `${delta >= 0 ? '+' : ''}${delta}%`,
      direction: delta > 0 ? 'up' : delta < 0 ? 'down' : 'neutral',
    },
    detail: `${reviews} reviews`,
  };
}

export interface ChartOptionItem {
  readonly label: string;
  readonly value: string;
}

export interface ChartOptionGroup {
  readonly label: string;
  readonly items: ReadonlyArray<ChartOptionItem>;
}

export const DATE_AXIS_KEYS: ReadonlyArray<string> = [
  'checkin_date', 'review_date', 'survey_date', 'departure_date',
];

export const CHART_AXIS_OPTIONS: ReadonlyArray<ChartOptionGroup> = [
  {
    label: 'Date',
    items: [
      { label: 'Check-in Date',  value: 'checkin_date' },
      { label: 'Review Date',    value: 'review_date' },
      { label: 'Survey Date',    value: 'survey_date' },
      { label: 'Departure Date', value: 'departure_date' },
    ],
  },
  {
    label: 'Dimension',
    items: [
      { label: 'Property',  value: 'property' },
      { label: 'KPI Names', value: 'kpi_names' },
      { label: 'Keywords',  value: 'top_keywords' },
    ],
  },
];

export type ChartAxisParentType = 'date_range' | 'source_platform' | 'properties' | 'kpis' | 'keywords';

export const CHART_AXIS_PARENT_OPTIONS: ReadonlyArray<{ label: string; value: ChartAxisParentType }> = [
  { label: 'Date Range',      value: 'date_range' },
  { label: 'Source Platform', value: 'source_platform' },
  { label: 'Properties',      value: 'properties' },
  { label: 'KPIs',            value: 'kpis' },
  { label: 'Keywords',        value: 'keywords' },
];

export const CHART_DATE_RANGE_OPTIONS: ReadonlyArray<ChartOptionItem> = [
  { label: 'Check-in Date',  value: 'checkin_date' },
  { label: 'Review Date',    value: 'review_date' },
  { label: 'Survey Date',    value: 'survey_date' },
  { label: 'Departure Date', value: 'departure_date' },
];

export const CHART_KPI_GROUP_OPTIONS: ReadonlyArray<ChartOptionItem> = [
  { label: 'Primary Service Rating', value: 'kpi:primary_service_rating' },
  { label: 'Room Quality',           value: 'kpi:room_quality' },
  { label: 'Restaurant and Bar',     value: 'kpi:restaurant_and_bar' },
  { label: 'Spa and Gym',            value: 'kpi:spa_and_gym' },
];

export type ChartSeriesDataType = 'percentage' | 'count';

export interface ChartSeriesOptionItem extends ChartOptionItem {
  readonly dataType: ChartSeriesDataType;
  readonly allowedChartTypes: ReadonlyArray<'bar' | 'bar-stacked' | 'line' | 'area'>;
}

const ALL_TYPES = ['bar', 'bar-stacked', 'line', 'area'] as const;
const BAR_ONLY  = ['bar', 'bar-stacked'] as const;
const NO_STACK  = ['bar', 'line', 'area'] as const;

export const NPS_MEASURE_KEYS: ReadonlySet<string> = new Set(['nps_score', 'nps_count']);
export const REVIEW_TYPE_MEASURE_KEYS: ReadonlySet<string> = new Set(['review_count']);
export const SENTIMENT_MEASURE_KEYS: ReadonlySet<string> = new Set(['sentiment_rating']);

const DATE_RANGE_MEASURES: ReadonlyArray<ChartSeriesOptionItem> = [
  { label: 'GuestRevu Rating',          value: 'gr_rating',            dataType: 'percentage', allowedChartTypes: ALL_TYPES },
  { label: 'Review Count',              value: 'review_count',         dataType: 'count',      allowedChartTypes: ALL_TYPES },
  { label: 'Online Review Rating',      value: 'online_review_rating', dataType: 'percentage', allowedChartTypes: ALL_TYPES },
  { label: 'Online Review Count',       value: 'online_review_count',  dataType: 'count',      allowedChartTypes: ALL_TYPES },
  { label: 'Guest Feedback Rating',     value: 'feedback_rating',      dataType: 'percentage', allowedChartTypes: ALL_TYPES },
  { label: 'Guest Feedback Count',      value: 'feedback_count',       dataType: 'count',      allowedChartTypes: ALL_TYPES },
  { label: 'Management Response Count', value: 'mgmt_response_count',  dataType: 'count',      allowedChartTypes: NO_STACK },
  { label: 'Sentiment Rating',          value: 'sentiment_rating',     dataType: 'percentage', allowedChartTypes: NO_STACK },
  { label: 'NPS Score',                 value: 'nps_score',            dataType: 'percentage', allowedChartTypes: ALL_TYPES },
  { label: 'NPS Count',                 value: 'nps_count',            dataType: 'count',      allowedChartTypes: ALL_TYPES },
];

const SOURCE_PLATFORM_MEASURES: ReadonlyArray<ChartSeriesOptionItem> = [
  { label: 'Rating',                    value: 'rating',               dataType: 'percentage', allowedChartTypes: ALL_TYPES },
  { label: 'Count',                     value: 'count',                dataType: 'count',      allowedChartTypes: ALL_TYPES },
  { label: 'Management Response Count', value: 'mgmt_response_count',  dataType: 'count',      allowedChartTypes: NO_STACK },
];

const KPI_MEASURES: ReadonlyArray<ChartSeriesOptionItem> = [
  { label: 'Rating (%)', value: 'rating_pct', dataType: 'percentage', allowedChartTypes: ALL_TYPES },
  { label: 'Count',      value: 'count',      dataType: 'count',      allowedChartTypes: ALL_TYPES },
];

export const CHART_MEASURES_BY_PARENT: Readonly<Record<ChartAxisParentType, ReadonlyArray<ChartSeriesOptionItem>>> = {
  date_range:      DATE_RANGE_MEASURES,
  source_platform: SOURCE_PLATFORM_MEASURES,
  properties:      DATE_RANGE_MEASURES,
  kpis:            KPI_MEASURES,
  keywords:        DATE_RANGE_MEASURES,
};

export const CHART_SERIES_INDEX: Readonly<Record<string, ChartSeriesOptionItem>> =
  (Object.values(CHART_MEASURES_BY_PARENT) as ReadonlyArray<ChartSeriesOptionItem>[])
    .flat()
    .reduce((acc, opt) => ({ ...acc, [opt.value]: opt }), {} as Record<string, ChartSeriesOptionItem>);

export const CHART_GROUP_BY_OPTIONS: ReadonlyArray<ChartOptionItem> = [
  { label: 'Source Platform', value: 'source_platform' },
  { label: 'Properties',      value: 'property' },
  { label: 'Keywords',        value: 'top_keywords' },
];

export interface MeasureGroupItem {
  readonly label: string;
  readonly value: string;
  readonly ratingKey?: string;
  readonly countKey?: string;
}

const DATE_RANGE_GROUPS: ReadonlyArray<MeasureGroupItem> = [
  { label: 'GuestRevu Rating',    value: 'gr_rating',       ratingKey: 'gr_rating' },
  { label: 'Review Count',        value: 'review_count',    countKey: 'review_count' },
  { label: 'Online Review',       value: 'online_review',  ratingKey: 'online_review_rating', countKey: 'online_review_count' },
  { label: 'Guest Feedback',      value: 'guest_feedback', ratingKey: 'feedback_rating',      countKey: 'feedback_count' },
  { label: 'Management Response', value: 'mgmt_response',                                     countKey: 'mgmt_response_count' },
  { label: 'Sentiment',           value: 'sentiment',      ratingKey: 'sentiment_rating' },
  { label: 'Net Promoter Score',  value: 'nps',            ratingKey: 'nps_score',            countKey: 'nps_count' },
];

const SOURCE_PLATFORM_GROUPS: ReadonlyArray<MeasureGroupItem> = [
  { label: 'Platform',            value: 'platform',       ratingKey: 'rating',               countKey: 'count' },
  { label: 'Management Response', value: 'mgmt_response',                                     countKey: 'mgmt_response_count' },
];

const KPI_GROUPS: ReadonlyArray<MeasureGroupItem> = [
  { label: 'Score',               value: 'kpi_score',      ratingKey: 'rating_pct',           countKey: 'count' },
];

export const MEASURE_GROUPS_BY_PARENT: Readonly<Record<ChartAxisParentType, ReadonlyArray<MeasureGroupItem>>> = {
  date_range:      DATE_RANGE_GROUPS,
  source_platform: SOURCE_PLATFORM_GROUPS,
  properties:      DATE_RANGE_GROUPS,
  kpis:            KPI_GROUPS,
  keywords:        DATE_RANGE_GROUPS,
};

export const LINKED_METRICS_BY_SOURCE: Partial<Record<DashboardChartSourceKey, ReadonlyArray<LinkedWidgetEntry>>> = {
  sentiment: [
    { metricKey: 'linked:sentiment-positive', label: 'Positive', icon: 'sentiment_satisfied',    tone: 'success', value: '64%', description: 'Percentage of reviews with positive sentiment.' },
    { metricKey: 'linked:sentiment-negative', label: 'Negative', icon: 'sentiment_dissatisfied', tone: 'danger',  value: '12%', description: 'Percentage of reviews with negative sentiment.' },
    { metricKey: 'linked:sentiment-neutral',  label: 'Neutral',  icon: 'sentiment_neutral',      tone: 'neutral', value: '24%', description: 'Percentage of reviews with neutral sentiment.' },
  ],
  'net-promoter-score': [
    { metricKey: 'linked:nps-promoters',  label: 'Promoters',  icon: 'thumb_up',   tone: 'success', value: '62%', description: 'Respondents scoring 9–10 (NPS promoters).' },
    { metricKey: 'linked:nps-detractors', label: 'Detractors', icon: 'thumb_down', tone: 'danger',  value: '14%', description: 'Respondents scoring 0–6 (NPS detractors).' },
    { metricKey: 'linked:nps-passives',   label: 'Passives',   icon: 'remove',     tone: 'neutral', value: '24%', description: 'Respondents scoring 7–8 (NPS passives).' },
  ],
};

export const GENERIC_LINKED_METRICS: ReadonlyArray<LinkedWidgetEntry> = [
  { metricKey: 'linked:stat-target',  label: 'Target',  icon: 'gps_fixed',    tone: 'brand',   value: '85%', description: 'Configured performance target.' },
  { metricKey: 'linked:stat-min',     label: 'Min',     icon: 'south',        tone: 'warning', value: '71%', description: 'Lowest data point in the current period.' },
  { metricKey: 'linked:stat-max',     label: 'Max',     icon: 'north',        tone: 'success', value: '96%', description: 'Highest data point in the current period.' },
  { metricKey: 'linked:stat-average', label: 'Average', icon: 'linear_scale', tone: 'brand',   value: '83%', description: 'Mean value across the current period.' },
];
